import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const emptyForm = { name: "", description: "", order: 0 };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchCategories = () => apiClient.get("/categories").then((res) => setCategories(res.data));
  useEffect(() => { fetchCategories(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) await apiClient.put(`/admin/categories/${editingId}`, form);
    else await apiClient.post("/admin/categories", form);
    toast.success(`Category ${editingId ? "updated" : "created"}`);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    fetchCategories();
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", order: c.order });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await apiClient.delete(`/admin/categories/${id}`);
    toast.success("Category deleted");
    fetchCategories();
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Categories</h1>
        <Button size="sm" className="rounded-md" onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }} data-testid="admin-new-category-btn"><Plus className="h-4 w-4 mr-1.5" /> New Category</Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mb-6 space-y-3" data-testid="admin-category-form">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-slate-900">{editingId ? "Edit Category" : "New Category"}</p>
            <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4 text-slate-400" /></button>
          </div>
          <Input placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-form-name" />
          <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-form-description" />
          <Input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0, 10) })} data-testid="category-form-order" />
          <Button type="submit" size="sm" className="rounded-md" data-testid="category-form-submit">Save</Button>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm divide-y divide-slate-100">
        {categories.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-4 py-3" data-testid={`admin-category-row-${c.slug}`}>
            <div>
              <p className="text-sm font-medium text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">{c.description}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleEdit(c)} data-testid={`admin-edit-category-${c.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(c.id)} data-testid={`admin-delete-category-${c.slug}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
